import { useState, useEffect, useRef } from "react";

const DEFAULT_FORM = {
  name: "",
  url: "",
  width: "",
  height: "",
  durationSeconds: "",
};

const toFormState = (creative) => {
  if (!creative) return { ...DEFAULT_FORM };
  return {
    name: creative.name ?? "",
    url: creative.url ?? "",
    width: creative.width != null ? String(creative.width) : "",
    height: creative.height != null ? String(creative.height) : "",
    durationSeconds:
      creative.durationSeconds != null ? String(creative.durationSeconds) : "",
  };
};

export default function CreativeForm({
  initialCreative,
  onSubmit,
  onCancel,
  performRequest,
}) {
  // Default to 'upload' when creating, 'url' when editing (creative already has a url).
  const [mode, setMode] = useState(() => (initialCreative ? "url" : "upload"));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [form, setForm] = useState(() => toFormState(initialCreative));
  const [error, setError] = useState("");
  const objectUrlRef = useRef(null);

  useEffect(() => {
    setForm(toFormState(initialCreative));
    setMode(initialCreative ? "url" : "upload");
    setFile(null);
    setError("");
    setUploading(false);
    setSubmitting(false);
    setUploadPct(0);
  }, [initialCreative]);

  // Revoke any outstanding object URL on unmount.
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const handleField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    if (!f.type.startsWith("video/")) {
      setError("Please choose a video file (mp4, etc.)");
      return;
    }

    // Revoke any previous object URL and clear stale dimensions so a failed
    // probe on the new file can't carry them over.
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setForm((prev) => ({
      ...prev,
      width: "",
      height: "",
      durationSeconds: "",
    }));

    setError("");
    setFile(f);

    // Default name to filename without extension if the name field is empty.
    if (!form.name.trim()) {
      const nameWithoutExt = f.name.replace(/\.[^.]+$/, "");
      setForm((prev) => ({ ...prev, name: nameWithoutExt }));
    }

    // Probe dimensions and duration without uploading.
    const v = document.createElement("video");
    v.preload = "metadata";
    objectUrlRef.current = URL.createObjectURL(f);
    v.onloadedmetadata = () => {
      setForm((prev) => ({
        ...prev,
        width: v.videoWidth ? String(v.videoWidth) : prev.width,
        height: v.videoHeight ? String(v.videoHeight) : prev.height,
        durationSeconds:
          Number.isFinite(v.duration) && v.duration > 0
            ? String(Math.round(v.duration))
            : prev.durationSeconds,
      }));
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
    v.onerror = () => {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
    v.src = objectUrlRef.current;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toPositiveInt = (v) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const name = form.name.trim();
    const width = toPositiveInt(form.width);
    const height = toPositiveInt(form.height);
    const durationSeconds = toPositiveInt(form.durationSeconds);

    try {
      setSubmitting(true);

      if (mode === "upload") {
        if (!file) {
          setError("Select a video file");
          return;
        }

        setUploading(true);
        setError("");

        const { uploadUrl, publicUrl, requiredHeaders } = await performRequest(
          "/creatives/upload-url",
          {
            method: "POST",
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
            }),
          },
        );

        // PUT the raw file bytes with upload progress (fetch has no upload progress API).
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader("Content-Type", file.type);
          Object.entries(requiredHeaders || {}).forEach(([k, v]) =>
            xhr.setRequestHeader(k, v),
          );
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable)
              setUploadPct(Math.round((ev.loaded / ev.total) * 100));
          };
          xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300
              ? resolve()
              : reject(
                  new Error(
                    "Upload failed (" +
                      xhr.status +
                      ")" +
                      (xhr.responseText
                        ? ": " + xhr.responseText.slice(0, 200)
                        : ""),
                  ),
                );
          xhr.onerror = () => reject(new Error("Upload failed (network/CORS)"));
          xhr.send(file);
        });

        const payload = { url: publicUrl };
        if (name) payload.name = name;
        if (width != null) payload.width = width;
        if (height != null) payload.height = height;
        if (durationSeconds != null) payload.durationSeconds = durationSeconds;

        await onSubmit(payload);
      } else {
        // URL mode — validate before submitting.
        if (!form.url.trim()) {
          setError("URL is required");
          return;
        }
        try {
          new URL(form.url.trim());
        } catch {
          setError("Please enter a valid URL (including https://)");
          return;
        }

        setError("");

        const payload = { url: form.url.trim() };
        if (name) payload.name = name;
        if (width != null) payload.width = width;
        if (height != null) payload.height = height;
        if (durationSeconds != null) payload.durationSeconds = durationSeconds;

        await onSubmit(payload);
      }
    } catch (err) {
      setError(err?.message || "Failed to save creative");
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  return (
    <form className="builder-form" onSubmit={handleSubmit}>
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      {/* Mode toggle — only shown when creating (no initialCreative) */}
      {!initialCreative && (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            marginBottom: "1.25rem",
          }}
        >
          <button
            type="button"
            className={`btn ${mode === "upload" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => {
              setMode("upload");
              setError("");
            }}
            disabled={submitting || uploading}
          >
            Upload file
          </button>
          <button
            type="button"
            className={`btn ${mode === "url" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => {
              setMode("url");
              setError("");
            }}
            disabled={submitting || uploading}
          >
            Paste URL
          </button>
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="creative-name">Name (optional)</label>
        <input
          id="creative-name"
          value={form.name}
          onChange={(e) => handleField("name", e.target.value)}
          placeholder="e.g. Spring banner 1080p"
        />
      </div>

      {mode === "upload" ? (
        <div className="form-group-modal">
          <label htmlFor="creative-file">Video file *</label>
          <input
            id="creative-file"
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            disabled={submitting || uploading}
          />
          {file && (
            <p className="form-help-text" style={{ marginTop: "0.4rem" }}>
              {file.name}
              {form.width && form.height
                ? ` · ${form.width}×${form.height}`
                : ""}
              {form.durationSeconds ? ` · ${form.durationSeconds}s` : ""}
            </p>
          )}
          {uploading && (
            <p className="form-help-text" style={{ marginTop: "0.4rem" }}>
              Uploading… {uploadPct}%
            </p>
          )}
        </div>
      ) : (
        <div className="form-group-modal">
          <label htmlFor="creative-url">URL *</label>
          <input
            id="creative-url"
            value={form.url}
            onChange={(e) => handleField("url", e.target.value)}
            placeholder="https://cdn.example.com/ad.mp4"
            required
          />
        </div>
      )}

      <div className="builder-grid three-columns">
        <div className="form-group-modal">
          <label htmlFor="creative-duration">Duration (s)</label>
          <input
            id="creative-duration"
            type="number"
            min="1"
            value={form.durationSeconds}
            onChange={(e) => handleField("durationSeconds", e.target.value)}
            placeholder="e.g. 30"
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="creative-width">Width (px)</label>
          <input
            id="creative-width"
            type="number"
            min="1"
            value={form.width}
            onChange={(e) => handleField("width", e.target.value)}
            placeholder="e.g. 1920"
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="creative-height">Height (px)</label>
          <input
            id="creative-height"
            type="number"
            min="1"
            value={form.height}
            onChange={(e) => handleField("height", e.target.value)}
            placeholder="e.g. 1080"
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-end",
          marginTop: "1.5rem",
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting || uploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || uploading}
        >
          {uploading
            ? `Uploading… ${uploadPct}%`
            : submitting
              ? initialCreative
                ? "Saving…"
                : "Creating…"
              : initialCreative
                ? "Save Changes"
                : "Create Creative"}
        </button>
      </div>
    </form>
  );
}
